/**
 * Verify Email Form Handler
 * Handles verification code input and form submission
 */

function initVerifyEmailForm() {
  const verifyEmailForm = document.querySelector('.verify-email-form');

  if (!verifyEmailForm) {
    return;
  }

  // Update email address display from sessionStorage
  const emailAddressElement = verifyEmailForm.querySelector('.email-address');
  const pendingEmail = sessionStorage.getItem('pendingVerificationEmail');
  if (emailAddressElement && pendingEmail) {
    emailAddressElement.textContent = pendingEmail;
  } else if (!pendingEmail) {
    // If no email in sessionStorage, redirect back to sign-up
    window.location.href = 'sign-up.html';
    return;
  }

  // Clear input button
  const clearButton = verifyEmailForm.querySelector('.input-clear');
  const codeInput = verifyEmailForm.querySelector('#verificationCode');

  if (clearButton && codeInput) {
    clearButton.addEventListener('click', function () {
      codeInput.value = '';
      codeInput.focus();
    });

    // Show/hide clear button based on input value
    function toggleClearButton() {
      clearButton.style.display = codeInput.value ? 'flex' : 'none';
    }

    codeInput.addEventListener('input', toggleClearButton);
    codeInput.addEventListener('focus', toggleClearButton);
    toggleClearButton(); // Initial check
  }

  // Resend code link
  const resendCodeLink = verifyEmailForm.querySelector('.resend-code-link');
  if (resendCodeLink) {
    resendCodeLink.addEventListener('click', async function (e) {
      e.preventDefault();
      
      // Get email from sessionStorage
      const email = sessionStorage.getItem('pendingVerificationEmail');
      
      if (!email) {
        alert('Session expired. Please register again.');
        window.location.href = 'sign-up.html';
        return;
      }

      // Disable link during request to prevent multiple clicks
      const originalText = resendCodeLink.textContent;
      resendCodeLink.style.pointerEvents = 'none';
      resendCodeLink.textContent = 'Sending...';

      try {
        // Get API base URL from global scope (defined in main.js)
        const apiBaseUrl = typeof API_BASE_URL !== 'undefined' ? API_BASE_URL : (window.API_BASE_URL || 'https://api.electhq.net');
        
        const response = await fetch(`${apiBaseUrl}/admins/register/resend-code`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            email: email,
          }),
        });

        let responseData = {};
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
          responseData = await response.json();
        }

        if (response.ok) {
          // Success: Show success message
          alert('Verification code has been resent to your email.');
          
          // Update code sent status
          const codeSentStatus = verifyEmailForm.querySelector('.code-sent-status');
          if (codeSentStatus) {
            codeSentStatus.textContent = 'Code sent!';
          }
        } else {
          // Handle errors
          const errorMessage = responseData?.message || 'Failed to resend code. Please try again.';
          alert(errorMessage);
        }
      } catch (error) {
        console.error('Resend code error:', error);
        
        // Get API base URL for debug info
        const apiBaseUrl = typeof API_BASE_URL !== 'undefined' ? API_BASE_URL : (window.API_BASE_URL || 'https://api.electhq.net');
        
        // Use shared error modal utility
        if (typeof window.showErrorModal === 'function') {
          await window.showErrorModal(error, `${apiBaseUrl}/admins/register/resend-code`, {
            title: 'Resend Code Error',
            message: 'An error occurred while resending the code. Please try again.',
            additionalInfo: {
              formType: 'verify-email-resend',
              email: email,
            },
          });
        } else {
          // Fallback if utility not loaded
          alert('An error occurred while resending the code. Please try again.');
        }
      } finally {
        // Re-enable link
        resendCodeLink.style.pointerEvents = 'auto';
        resendCodeLink.textContent = originalText;
      }
    });
  }

  // Form submission handler
  verifyEmailForm.addEventListener('submit', async function (e) {
    e.preventDefault();

    const code = codeInput.value.trim();

    if (!code) {
      alert('Please enter the verification code.');
      codeInput.focus();
      return;
    }

    // Get email and password from sessionStorage (saved during registration)
    const email = sessionStorage.getItem('pendingVerificationEmail');
    const password = sessionStorage.getItem('pendingVerificationPassword');

    if (!email || !password) {
      alert('Session expired. Please register again.');
      window.location.href = 'sign-up.html';
      return;
    }

    const submitButton = verifyEmailForm.querySelector('.btn-verify-email');
    const originalButtonText = submitButton.textContent;
    submitButton.disabled = true;
    submitButton.textContent = 'Verifying...';

    try {
      // Get API base URL from global scope (defined in main.js)
      const apiBaseUrl = typeof API_BASE_URL !== 'undefined' ? API_BASE_URL : (window.API_BASE_URL || 'https://api.electhq.net');
      
      const response = await fetch(`${apiBaseUrl}/admins/register/verify`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: email,
          password: password,
          code: code,
        }),
      });

      let responseData = {};
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        responseData = await response.json();
      }

      if (response.ok) {
        // Success: Email verified, extract token from response
        const token = responseData.token || responseData.accessToken || responseData.access_token || responseData.data?.token;
        
        // Clear session storage
        sessionStorage.removeItem('pendingVerificationEmail');
        sessionStorage.removeItem('pendingVerificationPassword');
        
        if (token) {
          // Get admin base URL from global scope (defined in main.js)
          const adminBaseUrl = typeof ADMIN_BASE_URL !== 'undefined' ? ADMIN_BASE_URL : (window.ADMIN_BASE_URL || 'https://admin-dev.electhq.net');
          
          // Redirect to admin panel with token
          window.location.href = `${adminBaseUrl}?token=${encodeURIComponent(token)}`;
        } else {
          // Token not found in response
          console.error('Token not found in response:', responseData);
          alert('Verification successful but token not received. Please contact support.');
        }
      } else {
        // Handle errors
        const errorMessage = responseData?.message || 'Invalid verification code. Please try again.';
        alert(errorMessage);
        codeInput.value = '';
        codeInput.focus();
      }
    } catch (error) {
      console.error('Verification error:', error);
      
      // Get API base URL for debug info
      const apiBaseUrl = typeof API_BASE_URL !== 'undefined' ? API_BASE_URL : (window.API_BASE_URL || 'https://api.electhq.net');
      
      // Use shared error modal utility
      if (typeof window.showErrorModal === 'function') {
        await window.showErrorModal(error, `${apiBaseUrl}/admins/register/verify`, {
          title: 'Verification Error',
          message: 'Verification failed. Please try again.',
          additionalInfo: {
            formType: 'verify-email',
            email: email,
          },
        });
      } else {
        // Fallback if utility not loaded
        alert('Verification failed. Please try again.');
      }
    } finally {
      // Re-enable submit button
      submitButton.disabled = false;
      submitButton.textContent = originalButtonText;
    }
  });
}

// Export to window for global access
window.initVerifyEmailForm = initVerifyEmailForm;

